/**
 * Batch Underwriting API service (Phase 203-02).
 *
 * Wraps the /api/v2/batch/* endpoints for the two-stage batch engine.
 * Uses the same axios instance pattern as api.ts.
 */

import axios from 'axios';
import type { BatchJobState } from '../types/batch';

const api = axios.create({
  baseURL: '/api/v2/batch',
  headers: {
    'Content-Type': 'application/json',
    'X-API-Key': import.meta.env.VITE_API_KEY || 'dev-key-12345',
  },
});

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface BatchQuotaResponse {
  calls_used: number;
  calls_remaining: number;
  monthly_limit: number;
  max_batch_size: number;
  default_top_n: number;
  max_age_days: number;
}

export interface SubmitBatchRequest {
  property_ids: number[];
  market_id?: string;
  max_age_days?: number;
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

/** Check RentCast quota before starting a batch job. */
export async function fetchBatchQuota(): Promise<BatchQuotaResponse> {
  const { data } = await api.get<BatchQuotaResponse>('/quota');
  return data;
}

/** Submit a new batch underwriting job (starts Stage 1). */
export async function submitBatchJob(
  request: SubmitBatchRequest
): Promise<BatchJobState> {
  const { data } = await api.post<BatchJobState>('/underwrite', request);
  return data;
}

/** Confirm Stage 2 deep underwriting for selected finalists. */
export async function confirmStage2(
  jobId: string,
  finalistIds: number[]
): Promise<BatchJobState> {
  const { data } = await api.post<BatchJobState>(`/${jobId}/confirm-stage2`, {
    finalist_ids: finalistIds,
  });
  return data;
}

/** Cancel a running batch job. */
export async function cancelBatchJob(jobId: string): Promise<void> {
  await api.delete(`/${jobId}`);
}
